import { BfiDataGrid, Column } from '@apply/ui';
import { DrawStatusBubble } from './DrawStatusBubble';
import { applicationInsights } from '../../../ApplicationInsights';
import CustomStore from 'devextreme/data/custom_store';
import { loanTypeDescription } from '@apply/constants';
import { FilterPanel, FilterRow } from 'devextreme-react/data-grid';
import { useLocation } from 'react-router-dom';
import { getDrawBudgetURL } from '../../../loans/common/LoanFunctions';
import dayjs from 'dayjs';
import { BfiNumberCell } from './BfiNumberCell';

interface DrawDashboardTableParameters {
    customStore: CustomStore;
}

export const DrawDashboardTable: React.FC<DrawDashboardTableParameters> = ({ customStore }) => {
    const location = useLocation();

    const openDraw = (e: any) => {
        window.location.href = getDrawBudgetURL(e.data.drawBudgetId, location);
    };

    return (
        <>
            <BfiDataGrid
                keyExpr='bfiNumber'
                dataStore={customStore}
                applicationInsights={applicationInsights}
                remoteOperations={false}
                onCellPrepared={(e) => {
                    if (e.rowType === 'header') {
                        e.cellElement.style.textAlign = 'left';
                    }
                }}
                onCellDblClick={openDraw}
                height={600}
            >
                <FilterRow visible={true} />
                <FilterPanel visible={true} />
                <Column
                    dataField='drawStatus'
                    caption='Status'
                    dataType='string'
                    width={60}
                    alignment='left'
                    allowFiltering={false}
                    cellRender={(
                        cellData: any // eslint-disable-line
                    ) => <DrawStatusBubble {...cellData.data} />}
                />
                <Column
                    dataField='entityName'
                    caption='Entity'
                    dataType='string'
                    width={150}
                />
                <Column
                    dataField='bfiNumber'
                    caption='Bfi #'
                    dataType='string'
                    alignment='right'
                    width={100}
                    cellRender={(
                        cellData: any // eslint-disable-line
                    ) => {
                        return <BfiNumberCell {...cellData.data} />;
                    }}
                />
                <Column
                    dataField='loanNumber'
                    caption='Loan #'
                    dataType='string'
                    alignment='right'
                    width={100}
                />

                <Column
                    dataField='loanType'
                    caption='Loan Type'
                    dataType='string'
                    width={100}
                    cellRender={(cellData: any) => loanTypeDescription[cellData.data.loanType]}
                    allowFiltering={false}
                />
                <Column
                    dataField='borrowerRequest'
                    caption='Request'
                    dataType='string'
                    format={{ type: 'currency', precision: 2 }}
                    alignment='right'
                    width={100}
                />
                <Column
                    dataField='funded'
                    caption='Funded'
                    dataType='string'
                    format={{ type: 'currency', precision: 2 }}
                    alignment='right'
                    width={100}
                />
                <Column
                    dataField='pointOfContact'
                    caption='PoC'
                    dataType='string'
                    width={140}
                />
                <Column
                    dataField='submitted'
                    caption='Submitted'
                    dataType='date'
                    alignment='center'
                    width={160}
                />
                <Column
                    dataField='inspectionOrdered'
                    caption='Inspection Ordered'
                    dataType='date'
                    alignment='center'
                    width={160}
                    cellRender={({ data }) => {
                        const { inspectionOrdered, inspectedOn } = data;

                        if (!inspectionOrdered) return;

                        const twoDaysAgo = dayjs().subtract(2, 'day');
                        const isOverTwoDaysOld = dayjs(inspectionOrdered).isBefore(twoDaysAgo);

                        const color = isOverTwoDaysOld && !inspectedOn ? 'red' : 'inherit';

                        return <span style={{ color: color }}>{dayjs(inspectionOrdered).format('MM/DD/YYYY')}</span>;
                    }}
                />
                <Column
                    dataField='fundedOn'
                    caption='Funded On'
                    dataType='date'
                    alignment='center'
                    width={160}
                />
                <Column
                    dataField='drawType'
                    caption='Draw Type'
                    dataType='string'
                    alignment='center'
                    width={100}
                    allowFiltering={false}
                    cellRender={(row: any) => {
                        switch (row.data.drawType) {
                            case 1:
                                return 'Automatic';
                            case 2:
                                return 'Manual';
                            default:
                                return 'Unknown';
                        }
                    }}
                />
                <Column
                    dataField='notes'
                    caption='Notes'
                    dataType='string'
                    alignment='left'
                    width={160}
                />
            </BfiDataGrid>
        </>
    );
};
